import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Chip,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Business, MeetingRoom, CheckCircle, Visibility, LocalHospital } from '@mui/icons-material'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { getTeacherChamberRoomScreensStatus } from 'src/consumer/services/featureServices'
import ChamberRoomStatusIndicator from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/components/ChamberRoomStatusIndicator'

const PromptContainer = styled(Box)`
  padding: 8px 0;
`

const InfoRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(116, 80, 170, 0.04);
  border-radius: 8px;
`

const IconBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(116, 80, 170, 0.1);
  color: rgba(116, 80, 170, 0.9);
`

const StatusBox = styled(Box)`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

export default function PageCheckInPrompt({
  open,
  onClose,
  onCheckIn,
  onPeekIn,
  checkingIn,
  availableRooms,
  selectedRoom,
  onRoomChange,
  selectedClinic,
  onClinicChange,
  availableClinics,
  mentorProfileData,
  isCheckedInOrPeekedIn,
  actionType,
  onActionTypeChange,
  selectedCheckInMethod,
  onCheckInMethodChange,
}) {
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now())
  const [screenStatuses, setScreenStatuses] = useState([])
  const [loadingStatuses, setLoadingStatuses] = useState(false)
  
  // Check if selected room is occupied by someone else
  const isRoomOccupied = selectedRoom?.occupied && selectedRoom?.checkedInMentorId !== mentorProfileData?.id
  
  // Get screen status for selected room
  const selectedRoomScreenStatus = useMemo(() => {
    if (!selectedRoom?.id) {
      console.log('[PAGE CHECK-IN PROMPT] No selected room ID')
      return 'unknown'
    }
    if (screenStatuses.length === 0) {
      console.log('[PAGE CHECK-IN PROMPT] No screen statuses available yet')
      return 'unknown'
    }
    const roomStatus = screenStatuses.find((status) => status.chamberRoomId === selectedRoom.id)
    console.log('[PAGE CHECK-IN PROMPT] Selected room status lookup:', {
      selectedRoomId: selectedRoom.id,
      roomStatus,
      allStatuses: screenStatuses.map(s => ({ chamberRoomId: s.chamberRoomId, status: s.status }))
    })
    if (!roomStatus) {
      console.log('[PAGE CHECK-IN PROMPT] Room not found in status list')
      return 'unknown'
    }
    const mappedStatus = roomStatus.status === 'up' ? 'online' : 'offline'
    console.log('[PAGE CHECK-IN PROMPT] Mapped status:', { original: roomStatus.status, mapped: mappedStatus })
    return mappedStatus
  }, [selectedRoom?.id, screenStatuses])
  
  // Get screen status for a specific room
  const getRoomScreenStatus = (roomId) => {
    if (!roomId || screenStatuses.length === 0) return 'unknown'
    const roomStatus = screenStatuses.find((status) => status.chamberRoomId === roomId)
    if (!roomStatus) return 'unknown'
    return roomStatus.status === 'up' ? 'online' : 'offline'
  }
  
  // Fetch screen statuses when dialog opens; abort in-flight requests when dialog closes or unmounts
  useEffect(() => {
    if (open) {
      const controller = new AbortController()
      const fetchScreenStatuses = async () => {
        if (controller.signal.aborted) return
        setLoadingStatuses(true)
        try {
          const statuses = await getTeacherChamberRoomScreensStatus(controller.signal)
          if (controller.signal.aborted) return
          console.log('[PAGE CHECK-IN PROMPT] Fetched screen statuses:', statuses)
          console.log('[PAGE CHECK-IN PROMPT] Available rooms:', availableRooms.map(r => ({ id: r.id, title: r.title || r.name })))
          setScreenStatuses(statuses)
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('[PAGE CHECK-IN PROMPT] Error fetching screen statuses:', error)
            setScreenStatuses([])
          }
        } finally {
          if (!controller.signal.aborted) setLoadingStatuses(false)
        }
      }

      fetchScreenStatuses()
      const statusInterval = setInterval(fetchScreenStatuses, 30000)
      return () => {
        clearInterval(statusInterval)
        controller.abort()
      }
    } else {
      setScreenStatuses([])
    }
  }, [open, availableRooms])
  
  // Filter check-in methods to only show those that are enabled and allowed for this mentor
  const checkInMethodOptions = useMemo(() => {
    if (!selectedRoom?.checkInMethodOptions) return []
    
    // Filter to only show enabled methods
    return selectedRoom.checkInMethodOptions.filter((method) => {
      // Only show methods that are enabled
      if (method?.enabled === false) return false
      
      // If method has an enabled property that's explicitly true, include it
      // If enabled is undefined/null, assume it's enabled (backward compatibility)
      return method?.enabled !== false
    })
  }, [selectedRoom?.checkInMethodOptions])

  // Track user interaction within the dialog
  const handleInteraction = () => {
    setLastInteractionTime(Date.now())
  }

  // Reset interaction time when dialog opens
  useEffect(() => {
    if (open) {
      setLastInteractionTime(Date.now())
    }
  }, [open])

  // Prevent closing on backdrop click or escape - only allow Cancel button
  const handleClose = (event, reason) => {
    // Don't allow closing while checking in
    if (checkingIn) {
      return
    }
    
    // Only allow closing via Cancel button (reason will be undefined/null for button clicks)
    // Completely block backdrop clicks and escape key
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return // Never close on backdrop or escape
    }
    
    // Allow closing only via Cancel button
    onClose()
  }

  // Auto-select first check-in method if only one option
  useEffect(() => {
    if (actionType === 'checkin' && checkInMethodOptions.length === 1 && !selectedCheckInMethod) {
      const firstMethod = checkInMethodOptions.find((m) => m?.enabled !== false)
      if (firstMethod && onCheckInMethodChange) {
        onCheckInMethodChange(firstMethod.id)
      }
    }
  }, [actionType, checkInMethodOptions, selectedCheckInMethod, onCheckInMethodChange])

  // Get theme and mobile status (needed before useEffect that uses isMobile)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Force remove blur from backdrop and drawer after render (handles timing issues)
  useEffect(() => {
    if (!open) {
      return
    }

    const removeBlur = () => {
      // Find all backdrop elements and remove blur
      const backdrops = document.querySelectorAll('.MuiBackdrop-root')
      backdrops.forEach((backdrop) => {
        if (backdrop) {
          backdrop.style.setProperty('backdrop-filter', 'none', 'important')
          backdrop.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
          backdrop.style.setProperty('background-color', 'rgba(0, 0, 0, 0.5)', 'important')
          // Ensure backdrop is below drawer content
          const backdropZIndex = parseInt(window.getComputedStyle(backdrop).zIndex) || 0
          if (backdropZIndex >= 1400) {
            backdrop.style.setProperty('z-index', '1399', 'important')
          }
        }
      })

      // Also remove blur from drawer paper and any other elements
      const drawerPapers = document.querySelectorAll('.MuiDrawer-paper, .MuiDialog-paper')
      drawerPapers.forEach((paper) => {
        if (paper) {
          paper.style.setProperty('backdrop-filter', 'none', 'important')
          paper.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
          // Ensure drawer content is above backdrop
          paper.style.setProperty('z-index', '1401', 'important')
          paper.style.setProperty('pointer-events', 'auto', 'important')
        }
      })

      // Remove blur from any fixed/absolute positioned overlays
      const allElements = document.querySelectorAll('*')
      allElements.forEach((el) => {
        const style = window.getComputedStyle(el)
        if (style.position === 'fixed' || style.position === 'absolute') {
          const zIndex = parseInt(style.zIndex) || 0
          // If element has blur and is in the z-index range of our drawer, remove blur
          if (zIndex >= 1300 && zIndex <= 1500) {
            const blurValue = style.backdropFilter || style.webkitBackdropFilter || 'none'
            if (blurValue !== 'none') {
              el.style.setProperty('backdrop-filter', 'none', 'important')
              el.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
            }
          }
        }
      })
    }

    // Remove blur immediately and on next frame (handles Material-UI timing)
    removeBlur()
    requestAnimationFrame(() => {
      requestAnimationFrame(removeBlur)
    })

    // Also check periodically in case Material-UI re-applies styles
    const interval = setInterval(() => {
      removeBlur()
    }, 50) // Check more frequently

    return () => clearInterval(interval)
  }, [open, isMobile])

  const handleActionSelect = (type) => {
    if (onActionTypeChange) {
      onActionTypeChange(type)
    }
    if (type === 'peekin') {
      if (!selectedRoom) {
        toast.error('Please select a room first')
        return
      }
    }
  }

  const handleProceed = () => {
    if (!selectedRoom) {
      toast.error('Please select a room')
      return
    }

    if (actionType === 'checkin') {
      if (checkInMethodOptions.length > 0 && !selectedCheckInMethod) {
        toast.error('Please select a check-in method')
        return
      }
      if (onCheckIn) {
        onCheckIn(selectedCheckInMethod)
      }
    } else if (actionType === 'peekin') {
      if (onPeekIn) {
        onPeekIn()
      }
    }
  }

  const canCheckIn = selectedRoom && !isRoomOccupied
  const canPeekIn = selectedRoom

  const renderContent = () => (
    <PromptContainer>
          {/* Show check-in status (only if checked in, not peeked in) */}
          {isCheckedInOrPeekedIn && isCheckedInOrPeekedIn.isCheckedIn && (
            <StatusBox
              sx={{
                bgcolor: 'success.light',
                border: '1px solid success.main',
              }}
            >
              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={500}>
                You are checked in
              </Typography>
            </StatusBox>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            To access this page, you need to check-in or peek-in to a clinic first.
          </Typography>

          {/* Clinic Selection */}
          {availableClinics && availableClinics.length > 0 && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Clinic</InputLabel>
                <Select
                  value={selectedClinic?.id || ''}
                  onChange={(e) => {
                    const clinic = availableClinics.find((c) => c.id === e.target.value)
                    if (clinic && onClinicChange) {
                      onClinicChange(clinic)
                      // Reset room and action when clinic changes
                      if (onRoomChange) onRoomChange(null)
                      if (onActionTypeChange) onActionTypeChange(null)
                      if (onCheckInMethodChange) onCheckInMethodChange(null)
                    }
                  }}
                  label="Select Clinic"
                >
                  {availableClinics.map((clinic) => (
                    <MenuItem key={clinic.id} value={clinic.id}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <LocalHospital sx={{ fontSize: 18 }} />
                        <Typography flex={1}>
                          {clinic.title || clinic.name || `Clinic ${clinic.id}`}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {/* Room Selection (only shown after clinic is selected) */}
          {selectedClinic && availableRooms.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth size="small">
                <InputLabel>Select Room</InputLabel>
                <Select
                  value={selectedRoom?.id || ''}
                  onChange={(e) => {
                    const room = availableRooms.find((r) => r.id === e.target.value)
                    if (room && onRoomChange) {
                      onRoomChange(room)
                      if (onCheckInMethodChange) onCheckInMethodChange(null)
                      if (onActionTypeChange) onActionTypeChange(null)
                    }
                  }}
                  label="Select Room"
                >
                  {availableRooms.map((room) => {
                    const roomOccupied = room.occupied && room.checkedInMentorId !== mentorProfileData?.id
                    const roomScreenStatus = getRoomScreenStatus(room.id)
                    return (
                      <MenuItem key={room.id} value={room.id}>
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                          <MeetingRoom sx={{ fontSize: 18 }} />
                          <Typography flex={1}>
                            {room.title || room.name || `Room ${room.id}`}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {loadingStatuses ? (
                              <CircularProgress size={12} sx={{ color: '#f59e0b' }} />
                            ) : (
                              <ChamberRoomStatusIndicator
                                status={roomScreenStatus}
                                chamberRoomName={room.title || room.name}
                                compact={true}
                              />
                            )}
                            {roomOccupied && (
                              <Chip
                                label="Unavailable for Check-in"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '10px', height: '20px' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              
              {/* Show screen status for selected room */}
              {selectedRoom && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Screen Status:
                  </Typography>
                  {loadingStatuses ? (
                    <CircularProgress size={12} sx={{ color: '#f59e0b' }} />
                  ) : (
                    <ChamberRoomStatusIndicator
                      status={selectedRoomScreenStatus}
                      chamberRoomName={selectedRoom.title || selectedRoom.name}
                      compact={true}
                    />
                  )}
                </Box>
              )}
            </>
          )}

          {selectedClinic && availableRooms.length === 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'error.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.main',
              }}
            >
              <Typography variant="body2" color="error.dark">
                No rooms available for this clinic. Please contact support.
              </Typography>
            </Box>
          )}

          {/* Show room occupied warning */}
          {isRoomOccupied && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" color="warning.dark">
                This room is currently occupied by another mentor. You can only peek-in to this room.
              </Typography>
            </Box>
          )}

          {/* Action selection: Peek-in or Check-in */}
          {selectedRoom && !actionType && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" fontWeight={500} sx={{ mb: 2 }}>
                Choose an action:
              </Typography>
              <Box display="flex" gap={2} sx={{ mb: 2 }}>
                <Button
                  variant={actionType === 'peekin' ? 'contained' : 'outlined'}
                  onClick={() => handleActionSelect('peekin')}
                  disabled={!canPeekIn}
                  sx={{
                    flex: 1,
                    borderColor: '#6779e3',
                    color: actionType === 'peekin' ? 'white' : '#6779e3',
                    bgcolor: actionType === 'peekin' ? '#6779e3' : 'transparent',
                    '&:hover': {
                      bgcolor: actionType === 'peekin' ? '#5568d3' : 'rgba(103, 121, 227, 0.1)',
                      borderColor: '#6779e3',
                    },
                  }}
                >
                  <Visibility sx={{ mr: 1, fontSize: 18 }} />
                  Peek In
                </Button>
                <Button
                  variant={actionType === 'checkin' ? 'contained' : 'outlined'}
                  onClick={() => handleActionSelect('checkin')}
                  disabled={!canCheckIn}
                  sx={{
                    flex: 1,
                    borderColor: '#7450aa',
                    color: actionType === 'checkin' ? 'white' : '#7450aa',
                    bgcolor: actionType === 'checkin' ? '#7450aa' : 'transparent',
                    '&:hover': {
                      bgcolor: actionType === 'checkin' ? '#633d8f' : 'rgba(116, 80, 170, 0.1)',
                      borderColor: '#7450aa',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(0, 0, 0, 0.26)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                >
                  <CheckCircle sx={{ mr: 1, fontSize: 18 }} />
                  Check In
                </Button>
              </Box>
            </>
          )}

          {/* Check-in method selection (only shown after selecting Check-in) */}
          {actionType === 'checkin' && !isRoomOccupied && checkInMethodOptions.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth size="small">
                <InputLabel>Check-In Method</InputLabel>
                <Select
                  value={selectedCheckInMethod || ''}
                  onChange={(e) => {
                    if (onCheckInMethodChange) {
                      onCheckInMethodChange(e.target.value)
                    }
                  }}
                  label="Check-In Method"
                >
                  {checkInMethodOptions
                    .filter((method) => method?.enabled !== false)
                    .map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.title || method.name || method.id}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '12px' }}>
            Both check-in and peek-in are recorded in our system.
          </Typography>
        </PromptContainer>
  )

  const renderActions = () => (
    <>
      <Button onClick={handleClose} variant="outlined" disabled={checkingIn}>
        Cancel
      </Button>
      {!selectedClinic ? (
        <Button variant="contained" disabled>
          Select a Clinic
        </Button>
      ) : availableRooms.length === 0 ? (
        <Button variant="contained" disabled>
          No Rooms Available
        </Button>
      ) : actionType ? (
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={checkingIn || (actionType === 'checkin' && checkInMethodOptions.length > 0 && !selectedCheckInMethod)}
          sx={{
            background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(116, 80, 170, 1) 0%, rgba(99, 102, 241, 1) 100%)',
            },
          }}
        >
          {checkingIn ? (
            <CircularProgress size={20} color="inherit" />
          ) : actionType === 'checkin' ? (
            'Check In'
          ) : (
            'Peek In'
          )}
        </Button>
      ) : (
        <Button variant="contained" disabled>
          Select an Action
        </Button>
      )}
    </>
  )

  // Mobile: Use Drawer from bottom
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={(event, reason) => {
          // Only allow closing via explicit user action
          if (reason && reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose(event, reason)
          }
        }}
        disableEscapeKeyDown={true}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            maxHeight: '95vh',
            minHeight: '200px', // Ensure minimum height so drawer is visible
            height: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.15)',
            width: '100%',
            maxWidth: '100%',
            zIndex: 1401, // Ensure it's above backdrop
          },
        }}
        ModalProps={{
          style: {
            zIndex: 1400,
          },
          disableScrollLock: false,
        }}
        sx={{
          zIndex: 1400,
          '& .MuiDrawer-paper': {
            zIndex: '1401 !important',
            pointerEvents: 'auto !important',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            // Remove backdrop filter on mobile to prevent blur issues
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: '1399 !important',
            pointerEvents: 'auto !important', // Allow backdrop to receive clicks (we handle closing in onClose)
          },
        }}
      >
        {/* Drag handle indicator */}
        <Box
          sx={{
            width: '40px',
            height: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '2px',
            margin: '12px auto 8px',
            cursor: 'grab',
            flexShrink: 0,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
          onClick={(e) => {
            handleInteraction()
            e.stopPropagation()
          }}
        >
          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Check-In Required
            </Typography>
          </Box>
          <Box sx={{ px: 2, flex: 1, overflow: 'auto', pb: 2 }}>
            {renderContent()}
          </Box>
          <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {renderActions()}
            </Box>
          </Box>
        </Box>
      </Drawer>
    )
  }

  // Desktop: Use Dialog
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
      BackdropProps={{
        onClick: (e) => {
          e.stopPropagation()
          // Prevent backdrop click
        },
        sx: {
          pointerEvents: 'auto',
          // Remove backdrop filter to prevent blur issues
          backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
          backdropFilter: 'none !important',
          WebkitBackdropFilter: 'none !important',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Check-In Required
      </DialogTitle>
      <DialogContent
        onClick={(e) => {
          handleInteraction()
          // Prevent clicks from bubbling to backdrop
          e.stopPropagation()
        }}
      >
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {renderActions()}
      </DialogActions>
    </Dialog>
  )
}

